import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  TableHead,
  TableContainer,
  Table,
  TableRow,
  TableBody,
  Paper,
  TableCell,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DIALOG_BOX_STYLE, TABLE_HEADER } from '../../../_constants/styleConst';
import { ExFMMessageInfo } from '../../../services/apiTypes';

type ExFMMessageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  messageInfo: ExFMMessageInfo;
};

const tableColumn = [
  { width: '30%', label: 'LBL_PARTS_CODE' },
  { width: '70%', label: 'LBL_PARTS_NAME' },
] as const;

/** ExFMエラーメッセージとパーツ一覧を表示するダイアログ */
const ExFMMessageModal: React.FC<ExFMMessageModalProps> = ({
  isOpen,
  onClose,
  messageInfo,
}) => {
  const { t } = useTranslation();

  const style = {
    cell: {
      p: '4px 8px',
    },
    cellText: {
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    },
  };

  return (
    <Modal
      open={isOpen}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          ...DIALOG_BOX_STYLE,
          maxWidth: '760px',
          maxHeight: '90dvh',
          p: '2rem 2rem 1rem',
        }}
      >
        <Box sx={{ maxHeight: '20dvh', overflow: 'auto' }}>
          <Typography
            variant="h6"
            component="h2"
            mr="5rem"
            sx={{ whiteSpace: 'pre-line', lineHeight: '1.4' }}
          >
            {/* 複数のメッセージがカンマ区切りで連結されているので改行に置換 */}
            {messageInfo.message.replaceAll(',', '\n')}
          </Typography>
        </Box>
        <Divider sx={{ width: '100%', my: '4px' }} />
        <Box sx={{ mt: '0.25rem', mb: '1rem' }}>
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              fontWeight: '500',
              lineHeight: '1.4',
              fontSize: '1rem',
            }}
          >
            {t('MSG_SEND_EXFM_PARTS')}
          </Typography>
        </Box>
        <Box sx={{ mt: '1rem' }}>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: '30dvh', overflow: 'auto' }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[...tableColumn].map((column, i) => (
                    <TableCell
                      key={`parts_header_${i}`}
                      sx={{
                        ...TABLE_HEADER,
                        width: column.width,
                      }}
                    >
                      {t(column.label)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {messageInfo.partsList.map((data, i) => (
                  <TableRow key={`parts_body_${i}`}>
                    <TableCell width={tableColumn[0].width} sx={style.cell}>
                      <Typography sx={style.cellText}>
                        {data.partsCode}
                      </Typography>
                    </TableCell>
                    <TableCell width={tableColumn[1].width} sx={style.cell}>
                      <Typography sx={style.cellText}>{data.name}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          <Box display="flex" justifyContent="end" mt="1rem">
            <Button
              onClick={onClose}
              variant="contained"
              sx={{ px: '1.5rem', minWidth: '150px' }}
            >
              {t('BTN_OK')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExFMMessageModal;
